import React, { Component } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody
} from 'reactstrap';
import numeral from 'numeral';

import DemographicCard from './prefecture/DemographicCard.js';
import EconomicCard from './prefecture/EconomicCard.js';
import RetailCard from './prefecture/RetailCard.js';
import ConsumptionCard from './prefecture/ConsumptionCard.js';
import Map from './prefecture/Map.js';
import Filter from './prefecture/Filter.js';
import CoreInsightCard from './prefecture/CoreInsightCard.js';
import BusinessTotalCard from './prefecture/BusinessTotalCard.js';
import HistoryControls from './shared/HistoryControls.js';
import MetricCard from './prefecture/components/MetricCard.js';

class Prefecture extends Component {
  constructor(props) {
    super(props);
    this.state = {
      prefecture: null,
      prefectureData: null,
      districtData: null,
      displayName: null,
      displayChineseName: null,
      currentDistricts: [],
      currentDistrictsData: null,
      districts: null,
      loading: false,
      dataPointNames: [],
      businessTotals: [],
      historyWindow: 'recent'
    };
  }

  componentDidMount() {
    const { prefectureId, comparablePrefectureId, business } = this.props;
    let id = prefectureId;

    if(comparablePrefectureId) {
      id = comparablePrefectureId;
    }

    if(id) {
      this.setState({ loading: true }, () => {
        fetch('/api/prefectures/' + id).then((resp) => resp.json())
          .then((respJson) => {
            this.setState({ prefecture: respJson.data }, () => {
              this.getPrefectureStats();
              this.getDistricts();
              this.getTier();
              if(business) {
                this.getBusinessTotals();
              }
            });
          });
      });
    }

    fetch('/api/data_exploration/getAllDataPointNames').then((resp) => resp.json())
      .then((respJson) => {
        this.setState({ dataPointNames: respJson });
      });
  }

  componentDidUpdate(prevProps, prevState) {
    if(this.state.currentDistricts && prevState.currentDistricts !== this.state.currentDistricts) {
      this.setState({ loading: true }, () => {
        this.getDistrictStats();
      });
    }
  }

  getPrefectureStats() {
    const { prefecture } = this.state;

    if(prefecture.relationships['year-stats'].data.length > 0) {
      fetch('/api/data_exploration/getPrefectureStats/' + prefecture.id).then((resp) => resp.json())
        .then((respJson) => {
          this.setState({ prefectureData: this.processStats(respJson) });
        });
    } else {
      this.setState({ prefectureData: null });
    }
  }

  getDistricts() {
    const { prefecture } = this.state;
    fetch(prefecture.relationships.districts.links.related + '?page[size]=100').then((resp) => resp.json())
      .then((respJson) => {
        const coreDistricts = [];
        respJson.data.forEach((district) => {
          if(district.attributes['subarea-type'] === 'Core District') {
            coreDistricts.push(district);
          }
        });

        this.setState({
          displayName: 'Core Districts',
          displayChineseName: '城区',
          currentDistricts: coreDistricts,
          districts: respJson.data
        });
      });
  }

  async getDistrictStats() {
    const { currentDistricts } = this.state;
    this.setState({ currentDistrictsData: {} });

    if(currentDistricts.length > 0) {
      const statFetches = currentDistricts.map(async (district) => {
        return await (await fetch('/api/data_exploration/getDistrictStats/' + district.id)).json();
      });

      Promise.all(statFetches).then((responses) => {
        let data = [];
        responses.forEach((response) => {
          data = data.concat(response);
        });
        data = this.processStats(data);

        this.setState({
          currentDistrictsData: data,
          loading: false
        });
      });
    } else {
      this.setState({
        currentDistrictsData: {},
        loading: false
      });
    }
  }

  getTier() {
    const { prefectureId, comparablePrefectureId } = this.props;
    let id = prefectureId;

    if(comparablePrefectureId) {
      id = comparablePrefectureId;
    }

    Promise.all([
      fetch('/api/find_comparable_markets/prefecture_tier/' + id).then((resp) => resp.json()),
      fetch('/api/find_comparable_markets/prefecture_index/' + id).then((resp) => resp.json())
    ])
      .then(([tierResp, indexResp]) => {
        const tier = tierResp.data.value;
        const index = parseFloat(indexResp.data.value).toFixed(3);

        this.setState({ tier, index });
      })
      .catch((error) => {
        console.error('Error fetching tier or index data:', error);
      });
  }

  processStats(rawData) {
    const aggregated = {};
    const formattedData = {};
    const weightedMetricNames = new Set(['% College Educated', 'Per Cap GDP', 'Avg H/H Savings', 'Per Cap Retail Sales']);
    const populationByDistrictYear = {};
    const weightedAggregates = {};
    const metricMeta = {};

    rawData.forEach((item) => {
      if(!item || !item.data_point_name || !item.year) {
        return;
      }

      metricMeta[item.data_point_name] = {
        multiplier: item.multiplier,
        unit: item.unit
      };

      if(item.data_point_name === 'Total Population') {
        populationByDistrictYear[item.id + '-' + item.year] = item.value;
      }

      if(weightedMetricNames.has(item.data_point_name)) {
        return;
      }

      if(!aggregated[item.data_point_name]) {
        aggregated[item.data_point_name] = {};
      }

      if(!aggregated[item.data_point_name][item.year]) {
        aggregated[item.data_point_name][item.year] = 0;
      }

      aggregated[item.data_point_name][item.year] += item.value;
    });

    rawData.forEach((item) => {
      if(!item || !weightedMetricNames.has(item.data_point_name) || !item.year) {
        return;
      }

      const population = populationByDistrictYear[item.id + '-' + item.year];
      if(population === undefined || population === null) {
        return;
      }

      if(!weightedAggregates[item.data_point_name]) {
        weightedAggregates[item.data_point_name] = {};
      }

      if(!weightedAggregates[item.data_point_name][item.year]) {
        weightedAggregates[item.data_point_name][item.year] = {
          weightedSum: 0,
          weight: 0
        };
      }

      weightedAggregates[item.data_point_name][item.year].weightedSum += item.value * population;
      weightedAggregates[item.data_point_name][item.year].weight += population;
    });

    Object.keys(aggregated).forEach((metricName) => {
      formattedData[metricName] = {};
      const meta = metricMeta[metricName] || {};

      Object.keys(aggregated[metricName]).forEach((year) => {
        const scaledValue = aggregated[metricName][year] * (meta.multiplier || 1);
        let value = this.formatValue(scaledValue);

        if(meta.unit === '%') {
          value += meta.unit;
        } else if(meta.unit) {
          value += ' ' + meta.unit;
        }

        formattedData[metricName][year] = value;
      });
    });

    Object.keys(weightedAggregates).forEach((metricName) => {
      formattedData[metricName] = {};
      const meta = metricMeta[metricName] || {};

      Object.keys(weightedAggregates[metricName]).forEach((year) => {
        const aggregate = weightedAggregates[metricName][year];
        if(!aggregate.weight) {
          return;
        }

        let averagedValue = aggregate.weightedSum / aggregate.weight;
        if(metricName === '% College Educated') {
          averagedValue = averagedValue * 100;
        } else {
          averagedValue = averagedValue * (meta.multiplier || 1);
        }

        let value = this.formatValue(averagedValue);
        if(metricName === '% College Educated' || meta.unit === '%') {
          value += '%';
        } else if(meta.unit) {
          value += ' ' + meta.unit;
        }

        formattedData[metricName][year] = value;
      });
    });

    return formattedData;
  }

  formatValue(value) {
    return numeral(value).format('0,0');
  }

  handleBackClick() {
    const { business, tier, coreInsightId, prefectureId } = this.props;

    if(business) {
      window.location.href = '/discover_unifying_factors/business/' + business;
    } else if(tier) {
      window.location.href = '/find_comparable_markets/tier/' + tier + '/prefecture/' + prefectureId;
    } else if(coreInsightId) {
      window.location.href = '/core_insights/insight/' + coreInsightId;
    } else {
      window.history.back();
    }
  }

  updatePrefecture(newState) {
    this.setState(newState);
  }

  getLatestPoint(dataSet, dataPointName) {
    if(dataSet && dataSet[dataPointName]) {
      const latestYear = Object.keys(dataSet[dataPointName]).sort((a, b) => b.localeCompare(a))[0];
      return {
        year: latestYear,
        value: dataSet[dataPointName][latestYear]
      };
    }

    return null;
  }

  getLatestValue(dataSet, dataPointName) {
    const point = this.getLatestPoint(dataSet, dataPointName);
    return point ? point.value : 'N/A';
  }

  getLatestDistrictYear(dataPointName) {
    const point = this.getLatestPoint(this.state.currentDistrictsData, dataPointName);
    return point ? point.year : 'N/A';
  }

  getHistoryAvailability() {
    const { currentDistrictsData } = this.state;
    if(!currentDistrictsData) return 0;

    return Object.keys(currentDistrictsData).reduce((maxYears, key) => {
      const yearCount = Object.keys(currentDistrictsData[key] || {}).length;
      return yearCount > maxYears ? yearCount : maxYears;
    }, 0);
  }

  getBusinessTotals() {
    const { prefecture } = this.state;
    const { business } = this.props;

    fetch('/api/data_exploration/getBusinessTotals/business/' + business + '/' + prefecture.id).then((resp) => resp.json())
      .then((respJson) => {
        this.setState({ businessTotals: respJson });
      });
  }

  render() {
    const { coreInsightId, business, category } = this.props;
    const {
      prefecture,
      prefectureData,
      tier,
      index,
      districts,
      displayName,
      currentDistricts,
      currentDistrictsData,
      loading,
      dataPointNames,
      businessTotals,
      historyWindow
    } = this.state;

    const totalPopulationYear = this.getLatestDistrictYear('Total Population');
    const perCapGdpYear = this.getLatestDistrictYear('Per Cap GDP');
    const savingsYear = this.getLatestDistrictYear('Avg H/H Savings');
    const retailYear = this.getLatestDistrictYear('Per Cap Retail Sales');
    const selectedDistrictCount = currentDistricts ? currentDistricts.length : 0;
    const totalDistrictCount = districts ? districts.length : 0;
    const spotlightMetrics = [
      { name: 'Total Population', yearData: currentDistrictsData && currentDistrictsData['Total Population'] },
      { name: 'Per Cap GDP', yearData: currentDistrictsData && currentDistrictsData['Per Cap GDP'] },
      { name: 'Per Cap Retail Sales', yearData: currentDistrictsData && currentDistrictsData['Per Cap Retail Sales'] }
    ].filter((metric) => metric.yearData);
    const headlineMetrics = [
      {
        label: 'Total Population',
        value: this.getLatestValue(currentDistrictsData, 'Total Population'),
        sublabel: 'Selected districts · ' + totalPopulationYear
      },
      {
        label: 'Per Cap GDP',
        value: this.getLatestValue(currentDistrictsData, 'Per Cap GDP'),
        sublabel: 'Weighted avg · ' + perCapGdpYear
      },
      {
        label: 'Avg H/H Savings',
        value: this.getLatestValue(currentDistrictsData, 'Avg H/H Savings'),
        sublabel: 'Weighted avg · ' + savingsYear
      },
      {
        label: 'Per Cap Retail Sales',
        value: this.getLatestValue(currentDistrictsData, 'Per Cap Retail Sales'),
        sublabel: 'Weighted avg · ' + retailYear
      }
    ];
    const mapSummaryItems = [
      {
        label: 'Total Land Area',
        value: this.getLatestValue(prefectureData, 'Land Area Sq.Km'),
        note: 'Full prefecture footprint'
      },
      {
        label: 'Built-Up Area',
        value: this.getLatestValue(currentDistrictsData, 'Land Area Sq.Km'),
        note: 'Active district selection'
      },
      {
        label: 'Viewing',
        value: displayName || 'Core Districts',
        note: selectedDistrictCount + ' of ' + totalDistrictCount + ' districts selected'
      }
    ];
    const noteData = prefecture && prefecture.relationships.note && prefecture.relationships.note.data
      ? prefecture.relationships.note.data
      : null;

    return (
      <Container fluid={true} className="mt-3 pref-dashboard-shell">
        <div className="pref-page-hero">
          <div className="pref-page-header pref-page-header-tight">
            <div className="pref-page-title-block">
              <div className="pref-page-kicker">District Analytics</div>
              <h1 className="pref-page-title">{prefecture ? prefecture.attributes.name : '—'}</h1>
              <p className="pref-page-subtitle mb-0">
                Prefecture · China{tier ? ' · Tier ' + tier : ''}
              </p>
            </div>
            <div className="pref-page-meta">
              {tier && <span className="pref-header-tier">Tier {tier}</span>}
              {index && (
                <div className="pref-header-index">
                  <span className="pref-index-value">{index}</span>
                  <span className="pref-index-label">Dev. Index</span>
                </div>
              )}
              <button className="btn pref-back-btn" onClick={this.handleBackClick.bind(this)}>
                ← Back
              </button>
              {prefecture && tier && (
                <a
                  className="btn pref-cta-btn"
                  href={'/find_comparable_markets/tier/' + tier + '/prefecture/' + prefecture.id}>
                  Comparable Markets
                </a>
              )}
            </div>
          </div>

          <div className="pref-page-hero-footer">
            <div className="pref-page-hero-copy">
              <div className="pref-page-hero-title">Interactive district workspace</div>
              <p className="pref-page-hero-text mb-0">
                District-level market performance across the selected geography and available time horizon.
              </p>
            </div>
            <div className="pref-page-hero-chips">
              <span className="pref-page-chip">{displayName || 'Core Districts'}</span>
              <span className="pref-page-chip">{selectedDistrictCount} selected</span>
              <span className="pref-page-chip">{this.getHistoryAvailability()} years available</span>
            </div>
          </div>
        </div>

        <Row className="mb-4 align-items-stretch pref-workspace-row">
          <Col xl="5" lg="12">
            <Card className="pref-workspace-card">
              <div className="pref-workspace-head">
                <div>
                  <div className="pref-workspace-kicker">Map Workspace</div>
                  <div className="pref-workspace-title">District selection map</div>
                  <p className="pref-workspace-copy mb-0">District geography and selection controls.</p>
                </div>
                <span className="pref-workspace-badge">Interactive</span>
              </div>

              {prefecture && (
                <div className="pref-workspace-controls">
                  <Filter
                    prefecture={prefecture}
                    districts={districts}
                    updatePrefecture={this.updatePrefecture.bind(this)} />
                </div>
              )}

              <div className="pref-workspace-map-shell">
                <CardBody className="pref-map-card-body">
                  {currentDistricts && currentDistrictsData ? (
                    <Map
                      prefecture={prefecture}
                      currentDistricts={currentDistricts}
                      districts={districts}
                      businessTotals={businessTotals}
                      updatePrefecture={this.updatePrefecture.bind(this)} />
                  ) : null}
                </CardBody>
              </div>

              <div className="pref-workspace-footer">
                {mapSummaryItems.map((item) => (
                  <div className="pref-workspace-stat" key={item.label}>
                    <div className="pref-workspace-stat-label">{item.label}</div>
                    <div className="pref-workspace-stat-value">{item.value}</div>
                    <div className="pref-workspace-stat-note">{item.note}</div>
                  </div>
                ))}
              </div>
            </Card>
          </Col>

          <Col xl="7" lg="12">
            <div className="pref-analytics-shell">
              <div className="pref-analytics-head">
                <div>
                  <div className="pref-analytics-kicker">Market Read</div>
                  <div className="pref-analytics-title">{displayName || 'Core Districts'} performance snapshot</div>
                  <p className="pref-analytics-copy mb-0">Headline indicators and trend views for the current district selection.</p>
                </div>
                <HistoryControls
                  historyWindow={historyWindow}
                  onChange={(nextWindow) => this.setState({ historyWindow: nextWindow })}
                  availableYears={this.getHistoryAvailability()} />
              </div>

              <div className="pref-kpi-grid">
                {headlineMetrics.map((metric) => (
                  <div className="pref-kpi-card pref-kpi-card-detailed" key={metric.label}>
                    <div className="pref-kpi-label">{metric.label}</div>
                    <div className="pref-kpi-value">{metric.value}</div>
                    <div className="pref-kpi-sub">{metric.sublabel}</div>
                  </div>
                ))}
              </div>

              <Card className="pref-spotlight-card">
                <CardBody>
                  <div className="pref-spotlight-head">
                    <div>
                      <div className="pref-spotlight-title">Trend Snapshot</div>
                      <p className="pref-spotlight-copy mb-0">Three high-signal metrics for {displayName || 'the current selection'} across the active time range.</p>
                    </div>
                    <span className="pref-spotlight-badge">{historyWindow.toUpperCase()}</span>
                  </div>
                  <div className="pref-spotlight-grid pref-spotlight-grid-wide">
                    {spotlightMetrics.map((metric) => (
                      <MetricCard
                        key={metric.name}
                        name={metric.name}
                        yearData={metric.yearData}
                        historyWindow={historyWindow}
                        compact={true} />
                    ))}
                  </div>
                </CardBody>
              </Card>
            </div>
          </Col>
        </Row>

        <DemographicCard
          currentDistrictsData={currentDistrictsData ? currentDistrictsData : {}}
          dataPointNames={dataPointNames}
          category={category}
          loading={loading}
          historyWindow={historyWindow} />
        <EconomicCard
          currentDistrictsData={currentDistrictsData ? currentDistrictsData : {}}
          dataPointNames={dataPointNames}
          category={category}
          loading={loading}
          historyWindow={historyWindow} />
        <RetailCard
          currentDistrictsData={currentDistrictsData ? currentDistrictsData : {}}
          dataPointNames={dataPointNames}
          category={category}
          loading={loading}
          historyWindow={historyWindow} />
        <ConsumptionCard
          currentDistrictsData={currentDistrictsData ? currentDistrictsData : {}}
          dataPointNames={dataPointNames}
          category={category}
          loading={loading}
          historyWindow={historyWindow} />

        {(noteData || coreInsightId || business) && (
          <div className="pref-context-grid">
            {noteData && (
              <div className="pref-context-panel pref-context-note">
                <div className="pref-context-kicker">Analyst Note</div>
                <div className="pref-context-title">{noteData.title}</div>
                <p className="pref-context-copy mb-0">{noteData.body}</p>
              </div>
            )}
            {coreInsightId && (
              <div className="pref-context-legacy">
                <CoreInsightCard coreInsightId={coreInsightId} />
              </div>
            )}
            {business && (
              <div className="pref-context-legacy">
                <BusinessTotalCard prefecture={prefecture} business={business} />
              </div>
            )}
          </div>
        )}
      </Container>
    );
  }
}

export default Prefecture;
